import { BigNumber } from '@ethersproject/bignumber'
import { parseUnits } from '@ethersproject/units'
import { Trans } from '@lingui/macro'
import { Currency, CurrencyAmount } from '@uniswap/sdk-core'
import { computePoolAddress, FeeAmount } from '@uniswap/v3-sdk'
import { useWeb3React } from '@web3-react/core'
import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import { AutoColumn } from 'components/Column'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import { RowBetween } from 'components/Row'
import { PageWrapper } from 'components/swap/styleds'
import { MouseoverTooltip } from 'components/Tooltip'
import { V3_CORE_FACTORY_ADDRESSES, V3_STAKER_ADDRESSES } from 'constants/addresses'
import { TransactionConfirmationContext } from 'contexts/TransactionConfirmationContext'
import { ApprovalState, useApproveCallback } from 'hooks/useApproveCallback'
import { useV3StakerContract } from 'hooks/useContract'
import { useTokenBalance } from 'lib/hooks/useCurrencyBalance'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Info } from 'react-feather'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import styled from 'styled-components/macro'

import { ThemedText } from '../../theme'
import { DateTimeContainer, DateTimeInput, DateTimeLabel, StyledInput, TimestampDisplay } from './styleds'

const CreateIncentiveWrapper = styled(PageWrapper)`
  padding: 68px 8px 0px;
  max-width: 640px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoint.md}px) {
    padding: 48px 8px 0px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.sm}px) {
    padding-top: 20px;
  }
`

const Label = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
`

const FeeSelector = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 16px;
  gap: 4px;
  flex-wrap: wrap;
`

const FeeButton = styled.button<{ active: boolean }>`
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme, active }) => (active ? theme.accentActive : theme.backgroundOutline)};
  background-color: ${({ theme, active }) => (active ? theme.accentActiveSoft : 'transparent')};
  color: ${({ theme, active }) => (active ? theme.accentActive : theme.textSecondary)};
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.accentAction};
    color: ${({ theme }) => theme.accentAction};
  }
`

const InputRow = styled.div`
  margin-bottom: 16px;
`

const AddressInput = styled.input`
  color: ${({ theme }) => theme.textPrimary};
  width: 100%;
  position: relative;
  font-weight: 500;
  outline: none;
  border: none;
  flex: 1 1 auto;
  background-color: ${({ theme }) => theme.deprecated_bg1};
  font-size: 16px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
`

const MaxButton = styled.button`
  background-color: ${({ theme }) => theme.accentAction};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  margin-left: 8px;
  margin-bottom: 8px;
`

const TimestampRow = styled(RowBetween)`
  gap: 12px;
`

export default function CreateIncentive() {
  const { account, chainId } = useWeb3React()
  const stakerContract = useV3StakerContract()
  const { transactionData, setTransactionData } = useContext(TransactionConfirmationContext)
  const addTransaction = useTransactionAdder()

  // Form state
  const [poolAddress, setPoolAddress] = useState<string>('')
  const [startTime, setStartTime] = useState<string>('')
  const [endTime, setEndTime] = useState<string>('')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [refundAddress, setRefundAddress] = useState<string>('')
  const [rewardAmount, setRewardAmount] = useState<string>('')
  const [selectedFee, setSelectedFee] = useState<FeeAmount | undefined>(undefined)

  // Replace token address strings with Currency objects
  const [token0, setToken0] = useState<Currency | null>(null)
  const [token1, setToken1] = useState<Currency | null>(null)
  const [rewardToken, setRewardToken] = useState<Currency | null>(null)

  // Format date for datetime-local input
  const formatDateForInput = (date: Date | null): string => {
    if (!date) return ''

    // Format as YYYY-MM-DDThh:mm in UTC
    const year = date.getUTCFullYear()
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')
    const hours = String(date.getUTCHours()).padStart(2, '0')
    const minutes = String(date.getUTCMinutes()).padStart(2, '0')

    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  // Parse datetime-local input value to Date object
  const parseInputToDate = (value: string): Date | null => {
    if (!value) return null

    // Parse input as UTC
    // The input format is YYYY-MM-DDThh:mm in local timezone
    // We need to create a UTC date from these components
    const [datePart, timePart] = value.split('T')
    const [year, month, day] = datePart.split('-').map(Number)
    const [hours, minutes] = timePart.split(':').map(Number)

    // Create date with UTC values
    const date = new Date()
    date.setUTCFullYear(year)
    date.setUTCMonth(month - 1) // Months are 0-indexed in JavaScript
    date.setUTCDate(day)
    date.setUTCHours(hours)
    date.setUTCMinutes(minutes)
    date.setUTCSeconds(0)
    date.setUTCMilliseconds(0)

    return date
  }

  // Convert Date to Unix timestamp (seconds since epoch)
  const dateToTimestamp = (date: Date | null): string => {
    if (!date) return ''
    return Math.floor(date.getTime() / 1000).toString()
  }

  // Convert Unix timestamp to Date
  const timestampToDate = (timestamp: string): Date | null => {
    if (!timestamp || isNaN(Number(timestamp))) return null

    // Create a Date from Unix timestamp (seconds since epoch) in UTC
    const date = new Date(0) // 0 = The epoch (1970-01-01T00:00:00Z)
    date.setUTCSeconds(Number(timestamp))
    return date
  }

  // Update timestamps when dates change
  useEffect(() => {
    if (startDate) {
      setStartTime(dateToTimestamp(startDate))
    }
  }, [startDate])

  useEffect(() => {
    if (endDate) {
      setEndTime(dateToTimestamp(endDate))
    }
  }, [endDate])

  // Update dates when timestamps are set directly
  useEffect(() => {
    if (startTime && !startDate) {
      setStartDate(timestampToDate(startTime))
    }
  }, [startTime, startDate])

  useEffect(() => {
    if (endTime && !endDate) {
      setEndDate(timestampToDate(endTime))
    }
  }, [endTime, endDate])

  // Handle currency selection
  const handleToken0Selected = useCallback((currency: Currency) => {
    setToken0(currency)
  }, [])

  const handleToken1Selected = useCallback((currency: Currency) => {
    setToken1(currency)
  }, [])

  const handleRewardTokenSelected = useCallback((currency: Currency) => {
    setRewardToken(currency)
  }, [])

  // Auto-compute pool address when tokens and fee are selected
  useEffect(() => {
    if (token0 && token1 && selectedFee !== undefined && chainId && V3_CORE_FACTORY_ADDRESSES[chainId]) {
      try {
        // Ensure tokens are in the correct order
        const [tokenA, tokenB] = token0.wrapped.sortsBefore(token1.wrapped)
          ? [token0.wrapped, token1.wrapped]
          : [token1.wrapped, token0.wrapped]

        const computedPoolAddress = computePoolAddress({
          factoryAddress: V3_CORE_FACTORY_ADDRESSES[chainId],
          tokenA,
          tokenB,
          fee: selectedFee,
        })
        setPoolAddress(computedPoolAddress)
      } catch (error) {
        console.error('Error computing pool address:', error)
      }
    }
  }, [token0, token1, selectedFee, chainId])

  // User's token balance
  const rewardTokenBalance = useTokenBalance(account ?? undefined, rewardToken?.wrapped)

  // Approval state for reward token
  const [approvalState, approve] = useApproveCallback(
    rewardToken && rewardAmount && !rewardToken.isNative
      ? CurrencyAmount.fromRawAmount(rewardToken, parseUnits(rewardAmount, rewardToken.decimals).toString())
      : undefined,
    chainId ? V3_STAKER_ADDRESSES[chainId] : undefined
  )

  // Validates if all required fields are filled
  const formIsValid = useMemo(() => {
    return (
      rewardToken !== null &&
      poolAddress !== '' &&
      startTime !== '' &&
      endTime !== '' &&
      Number(startTime) < Number(endTime) &&
      refundAddress !== '' &&
      rewardAmount !== '' &&
      parseFloat(rewardAmount) > 0 &&
      selectedFee !== undefined &&
      token0 !== null &&
      token1 !== null
    )
  }, [rewardToken, poolAddress, startTime, endTime, refundAddress, rewardAmount, selectedFee, token0, token1])

  // Handle max button click
  const onClickMax = useCallback(() => {
    if (rewardTokenBalance) {
      setRewardAmount(rewardTokenBalance.toExact())
    }
  }, [rewardTokenBalance])

  // Function to reset all form fields
  const resetFields = useCallback(() => {
    setPoolAddress('')
    setStartTime('')
    setEndTime('')
    setStartDate(null)
    setEndDate(null)
    setRefundAddress('')
    setRewardAmount('')
    setSelectedFee(undefined)
    setToken0(null)
    setToken1(null)
    setRewardToken(null)
  }, [])

  // Handle form submission to create incentive
  const handleCreateIncentive = useCallback(async () => {
    if (
      !stakerContract ||
      !rewardToken ||
      !startTime ||
      !endTime ||
      !account ||
      !token0 ||
      !token1 ||
      selectedFee === undefined
    ) {
      return
    }

    const incentiveKey = {
      rewardToken: rewardToken.isNative ? rewardToken.wrapped.address : rewardToken.address,
      pool: poolAddress,
      startTime: Number(startTime),
      endTime: Number(endTime),
      refundee: refundAddress || account,
    }

    // Set transaction state to attempting
    setTransactionData({
      showConfirm: true,
      attempting: true,
      pendingText: `Creating incentive...`,
      hash: '',
      txError: '',
    })

    try {
      const estimatedGas = await stakerContract.estimateGas.createIncentive(
        incentiveKey,
        parseUnits(rewardAmount, rewardToken.decimals).toString()
      )

      const tx = await stakerContract.createIncentive(
        incentiveKey,
        parseUnits(rewardAmount, rewardToken.decimals).toString(),
        {
          gasLimit: estimatedGas.mul(BigNumber.from(12)).div(BigNumber.from(10)),
        }
      )

      setTransactionData((transactionData) => ({
        ...transactionData,
        showConfirm: true,
        attempting: false,
        hash: tx.hash,
      }))

      addTransaction(tx, {
        type: TransactionType.CREATE_INCENTIVE,
      })

      resetFields()
    } catch (error) {
      console.error('Error creating incentive:', error)
      // Update transaction state with error
      setTransactionData((transactionData) => ({
        ...transactionData,
        showConfirm: true,
        attempting: false,
        txError: error?.reason ?? '',
      }))
    }
  }, [
    stakerContract,
    rewardToken,
    poolAddress,
    startTime,
    endTime,
    refundAddress,
    rewardAmount,
    account,
    token0,
    token1,
    selectedFee,
    setTransactionData,
    addTransaction,
    resetFields,
  ])

  return (
    <CreateIncentiveWrapper>
      <AutoColumn gap="lg">
        <ThemedText.LargeHeader>
          <Trans>Create Incentive</Trans>
        </ThemedText.LargeHeader>

        <LightCard>
          <AutoColumn gap="md">
            <InputRow>
              <Label>
                <Trans>Token Pair</Trans>
              </Label>
              <RowBetween style={{ gap: '12px' }}>
                <CurrencyInputPanel
                  id="token-0-input"
                  value=""
                  onUserInput={() => undefined}
                  hideInput={true}
                  onCurrencySelect={handleToken0Selected}
                  currency={token0}
                  showMaxButton={false}
                  showCommonBases={true}
                  otherCurrency={token1}
                />
                <CurrencyInputPanel
                  id="token-1-input"
                  value=""
                  onUserInput={() => undefined}
                  hideInput={true}
                  onCurrencySelect={handleToken1Selected}
                  currency={token1}
                  showMaxButton={false}
                  showCommonBases={true}
                  otherCurrency={token0}
                />
              </RowBetween>
              <ThemedText.BodySmall style={{ marginTop: '8px', opacity: 0.7 }}>
                <Trans>Select the token pair for the liquidity pool</Trans>
              </ThemedText.BodySmall>
            </InputRow>

            <InputRow>
              <Label>
                <Trans>Fee Tier</Trans>
              </Label>
              <FeeSelector>
                <FeeButton active={selectedFee === FeeAmount.LOWEST} onClick={() => setSelectedFee(FeeAmount.LOWEST)}>
                  0.01%
                </FeeButton>
                <FeeButton active={selectedFee === FeeAmount.LOW} onClick={() => setSelectedFee(FeeAmount.LOW)}>
                  0.05%
                </FeeButton>
                <FeeButton active={selectedFee === FeeAmount.MEDIUM} onClick={() => setSelectedFee(FeeAmount.MEDIUM)}>
                  0.3%
                </FeeButton>
                <FeeButton active={selectedFee === FeeAmount.HIGH} onClick={() => setSelectedFee(FeeAmount.HIGH)}>
                  1%
                </FeeButton>
              </FeeSelector>
            </InputRow>

            <InputRow>
              <Label>
                <Trans>Pool Address</Trans>
              </Label>
              <AddressInput
                value={poolAddress}
                onChange={(e) => setPoolAddress(e.target.value)}
                placeholder="0x..."
                disabled={!!token0 && !!token1 && selectedFee !== undefined}
              />
              <ThemedText.BodySmall style={{ marginTop: '8px', opacity: 0.7 }}>
                <Trans>Auto-computed from tokens and fee tier</Trans>
              </ThemedText.BodySmall>
            </InputRow>

            <InputRow>
              <Label>
                <Trans>Reward Token</Trans>
              </Label>
              <CurrencyInputPanel
                id="reward-token-input"
                value=""
                onUserInput={() => undefined}
                hideInput={true}
                onCurrencySelect={handleRewardTokenSelected}
                currency={rewardToken}
                showMaxButton={false}
                showCommonBases={true}
              />
              <ThemedText.BodySmall style={{ marginTop: '8px', opacity: 0.7 }}>
                <Trans>Select the token you want to use as reward</Trans>
              </ThemedText.BodySmall>
            </InputRow>

            <InputRow>
              <Label>
                <Trans>Reward Amount</Trans> {rewardToken?.symbol && `(${rewardToken.symbol})`}
                {rewardTokenBalance && (
                  <MaxButton onClick={onClickMax}>
                    <Trans>MAX</Trans>
                  </MaxButton>
                )}
              </Label>
              <StyledInput value={rewardAmount} onUserInput={setRewardAmount} placeholder="0.0" />
              {rewardTokenBalance && (
                <ThemedText.BodySmall style={{ marginTop: '8px' }}>
                  <Trans>Balance: {rewardTokenBalance.toSignificant(6)}</Trans>
                </ThemedText.BodySmall>
              )}
            </InputRow>

            <InputRow>
              <Label>
                <Trans>Time Range</Trans>
                <MouseoverTooltip text="Incentive start time must be in the future.">
                  <Info size={14} style={{ marginLeft: '6px', cursor: 'pointer' }} />
                </MouseoverTooltip>
              </Label>
              <TimestampRow>
                <div style={{ flex: 1 }}>
                  <DateTimeContainer>
                    <DateTimeLabel>
                      <ThemedText.BodySmall>
                        <Trans>Start</Trans>
                      </ThemedText.BodySmall>
                      <span
                        style={{
                          backgroundColor: 'rgba(114, 138, 150, 0.12)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                        }}
                      >
                        UTC time
                      </span>
                    </DateTimeLabel>
                    <DateTimeInput
                      type="datetime-local"
                      value={startDate ? formatDateForInput(startDate) : ''}
                      onChange={(e) => {
                        const newDate = parseInputToDate(e.target.value)
                        setStartDate(newDate)
                      }}
                    />
                    {startTime && (
                      <TimestampDisplay>
                        <Trans>Unix Timestamp (UTC): {startTime}</Trans>
                      </TimestampDisplay>
                    )}
                  </DateTimeContainer>
                </div>
                <div style={{ flex: 1 }}>
                  <DateTimeContainer>
                    <DateTimeLabel>
                      <ThemedText.BodySmall>
                        <Trans>End</Trans>
                      </ThemedText.BodySmall>
                      <span
                        style={{
                          backgroundColor: 'rgba(114, 138, 150, 0.12)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                        }}
                      >
                        UTC time
                      </span>
                    </DateTimeLabel>
                    <DateTimeInput
                      type="datetime-local"
                      value={endDate ? formatDateForInput(endDate) : ''}
                      onChange={(e) => {
                        const newDate = parseInputToDate(e.target.value)
                        setEndDate(newDate)
                      }}
                      min={startDate ? formatDateForInput(startDate) : ''}
                    />
                    {endTime && (
                      <TimestampDisplay>
                        <Trans>Unix Timestamp (UTC): {endTime}</Trans>
                      </TimestampDisplay>
                    )}
                  </DateTimeContainer>
                </div>
              </TimestampRow>
            </InputRow>

            <InputRow>
              <Label>
                <Trans>Refund Address</Trans>
              </Label>
              <AddressInput
                value={refundAddress}
                onChange={(e) => setRefundAddress(e.target.value)}
                placeholder={account || '0x...'}
              />
              <ThemedText.BodySmall style={{ marginTop: '8px', opacity: 0.7 }}>
                <Trans>Address where remaining rewards will be sent after incentive ends</Trans>
              </ThemedText.BodySmall>
            </InputRow>
          </AutoColumn>
        </LightCard>

        {approvalState !== ApprovalState.APPROVED && rewardToken ? (
          <ButtonPrimary onClick={approve} disabled={approvalState !== ApprovalState.NOT_APPROVED}>
            <Trans>Approve {rewardToken.symbol}</Trans>
          </ButtonPrimary>
        ) : (
          <ButtonPrimary onClick={handleCreateIncentive} disabled={!formIsValid || transactionData.attempting}>
            {transactionData.attempting ? <Trans>Creating...</Trans> : <Trans>Create Incentive</Trans>}
          </ButtonPrimary>
        )}
      </AutoColumn>
    </CreateIncentiveWrapper>
  )
}
